import axios from 'axios';
import { cacheAdapterEnhancer, Cache } from 'axios-extensions';
import store from '@/store';

import axiosInterceptors from './axios-interceptors';

const baseCacheConfig = {
  maxAge: 1000 * 60 * 15, // 15 minutes
  max: 1000,
};

class ApiService {
  constructor({
    baseUrl = store.getters.vmBaseUrl,
    addAuthToken = true,
    cacheConfig = baseCacheConfig,
    disableCache = false,
    headers = null,
  } = {}) {
    this.cache = disableCache ? undefined : new Cache(cacheConfig);

    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      headers: headers || {
        [store.state.config.schemaHeaderName]: GLOBALS.SCHEMA,
      },
      adapter: disableCache ? axios.defaults.adapter : cacheAdapterEnhancer(axios.defaults.adapter),
      cache: this.cache,
    });

    this.addRequestInterceptor(axiosInterceptors.removeEmptyParams);
    if (!disableCache) {
      this.addResponseInterceptor(axiosInterceptors.clearCache(this.cache));
    }

    if (addAuthToken) {
      this.addRequestInterceptor(axiosInterceptors.addAuthTokenInterceptor);
    }
  }

  clearCache() {
    if (this.cache) {
      this.cache.reset();
    }
  }

  addRequestInterceptor(interceptor) {
    this.axiosInstance.interceptors.request.use(interceptor);
  }

  addResponseInterceptor(interceptor) {
    this.axiosInstance.interceptors.response.use(interceptor);
  }

  setBaseUrl(newBaseURL) {
    this.axiosInstance.defaults.baseURL = newBaseURL;
  }

  extendBaseUrl(urlSuffix) {
    this.setBaseUrl(this.axiosInstance.defaults.baseURL + urlSuffix);
  }

  get(url, config) {
    return this.axiosInstance.get(url, config);
  }

  post(url, data, config) {
    return this.axiosInstance.post(url, data, config);
  }

  put(url, data, config) {
    return this.axiosInstance.put(url, data, config);
  }

  patch(url, data, config) {
    return this.axiosInstance.patch(url, data, config);
  }

  delete(url, config) {
    return this.axiosInstance.delete(url, config);
  }

  customRequest(config) {
    return this.axiosInstance.request(config);
  }
}

export default ApiService;
